import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { theme } from "../theme";
import { useAddRoleMutation, useListPeopleQuery } from "../apis/rolesAPI";
import { getOrganisation } from "../slices/organisationSlice";
import { selector } from "../store";
import { useNavigate } from "react-router-dom";
import colors from "../colors";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { IRole, IUserStatusType } from "@backend/model/role";
import { ViewContainer } from "./components/viewContainer";
import { useTranslation } from "react-i18next";
import { useListSettingsQuery } from "../apis/settingsAPI";
import {
  filterSettings,
  isIncludedSettingEnabled as isSettingEnabled,
} from "../utils/settingUtils";
import {
  useInviteToCourseMutation,
  useListCoursesQuery,
} from "../apis/coursesAPI";
import { InviteModal } from "./components/modals/inviteModal";
import { ImageUploadField } from "./components/ImageUploadField";

export const AddPeople = () => {
  const org: any = selector((state) => getOrganisation(state));

  const navigete = useNavigate();
  const { t } = useTranslation();

  const [userStatus, setUserStatus] = useState<IUserStatusType>("employee");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [company, setCompany] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | undefined>();
  const [occupationalSafetyCard, setOccupationalSafetyCard] =
    useState<string>("");
  const [hotWorkCard, setHotWorkCard] = useState<string>("");
  const [occupationalSafetyCardImage, setOccupationalSafetyCardImage] =
    useState<string>("");
  const [firstAidCardImage, setFirstAidCardImage] = useState<string>("");
  const [hotWorkCardImage, setHotWorkCardImage] = useState<string>("");
  const [shoeSize, setShoeSize] = useState("");
  const [jacketSize, setJacketSize] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [visitationDate, setVisitationDate] = useState("");

  const [error, setError] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [addRole, { isLoading: addRoleLoading }] = useAddRoleMutation();

  const { data: settings, isFetching: loadingSettings } = useListSettingsQuery({
    organisationId: org?.organisationId || "",
  });
  const { data: courses, isLoading: coursesLoading } = useListCoursesQuery(
    org?.organisationId,
    {
      skip: !org,
    }
  );
  // Get users, to check emails 
  const { data: people, isFetching: loadingPeople } = useListPeopleQuery(org!, {
      skip: !org,
    });

  // Check if any role in the people list already uses the entered email or phone number
  const emailAlreadyInUse =
    email !== "" &&
    !!people?.some(
      (person) =>
        person.email != null &&
        person.email.trim().toLowerCase() === email.trim().toLowerCase()
    );
  const phoneAlreadyInUse =
    phoneNumber !== "" &&
    !!people?.some(
      (person) =>
        person.phoneNumber != null &&
        person.phoneNumber.trim() === phoneNumber.trim()
    );

  // Filter settings by user status
  const filteredSettings = filterSettings(userStatus, settings || []);

  const [invite] = useInviteToCourseMutation();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserStatus(
      ((event.target as HTMLInputElement).value as IUserStatusType) ||
        "employee"
    );
  };

  const onAdd = useCallback(async () => {
    if (org) {
      try {
        await addRole({
          organisationId: org.organisationId,
          firstName,
          lastName,
          email,
          company,
          phoneNumber,
          taxNumber,
          dateOfBirth: dateOfBirth?.toDate(),
          occupationalSafetyCard,
          hotWorkCard,
          role: "user",
          userStatus: userStatus || "employee",
          occupationalSafetyCardImage,
          firstAidCardImage,
          hotWorkCardImage,
          shoeSize,
          jacketSize,
          dietaryRestrictions,
          visitationDate,
        } as IRole).unwrap();
        navigete(-1);
      } catch (err) {
        setError(true);
      }
    }
  }, [
    addRole,
    org,
    firstName,
    lastName,
    email,
    company,
    phoneNumber,
    taxNumber,
    dateOfBirth,
    navigete,
    userStatus,
    occupationalSafetyCard,
    hotWorkCard,
    occupationalSafetyCardImage,
    firstAidCardImage,
    hotWorkCardImage,
    shoeSize,
    jacketSize,
    dietaryRestrictions,
    visitationDate,
  ]);

  const onAddAndInvite = useCallback(
    async (courses: string[]) => {
      if (org) {
        try {
          // Add a role
          const result: IRole = await addRole({
            organisationId: org.organisationId,
            firstName,
            lastName,
            email,
            company,
            phoneNumber,
            taxNumber,
            dateOfBirth: dateOfBirth?.toDate(),
            occupationalSafetyCard,
            hotWorkCard,
            role: "user",
            userStatus: userStatus || "employee",
            occupationalSafetyCardImage,
            firstAidCardImage,
            hotWorkCardImage,
            shoeSize,
            jacketSize,
            dietaryRestrictions,
            visitationDate,
          } as IRole).unwrap();

          // If a person was successfully added, invite it to selected courses
          if (result) {
            await Promise.all(
              courses.map(async (course) => {
                await invite({
                  organisationId: org?.organisationId || "",
                  courseId: course,
                  userIds: [result.id], // Only invite the current person.
                }).unwrap();
              })
            );
            navigete(-1);
            // go back after inviting and saving
          }
        } catch (err) {
          setError(true);
        }
      }
    },
    [
      addRole,
      org,
      firstName,
      lastName,
      email,
      company,
      phoneNumber,
      taxNumber,
      dateOfBirth,
      userStatus,
      occupationalSafetyCard,
      hotWorkCard,
      occupationalSafetyCardImage,
      firstAidCardImage,
      hotWorkCardImage,
      shoeSize,
      jacketSize,
      dietaryRestrictions,
      visitationDate,
      invite,
    ]
  );

  return (
    <ViewContainer loading={loadingSettings || addRoleLoading}>
      <Box
        width={"100%"}
        height={"100%"}
        position={"relative"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"flex-start"}
      >
        <Snackbar
          open={error}
          autoHideDuration={3000}
          onClose={() => setError(false)}
        >
          <Alert
            onClose={() => setError(false)}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {t("errorOccured")}
          </Alert>
        </Snackbar>
        <Typography variant="h4">Add people</Typography>
        <Divider style={{ width: "100%", marginTop: theme.spacing(2) }} />

        <FormControl>
          <FormLabel id="user-status-label">Status</FormLabel>
          <RadioGroup
            aria-labelledby="user-status-label"
            defaultValue="employee"
            name="user-status"
            value={userStatus}
            onChange={handleChange}
          >
            <FormControlLabel
              value={"employee"}
              control={<Radio />}
              label="Employee"
            />
            <FormControlLabel
              value={"contractor"}
              control={<Radio />}
              label="Contractor"
            />
            <FormControlLabel
              value={"visitor"}
              control={<Radio />}
              label="Visitor"
            />
          </RadioGroup>
        </FormControl>

        <TextField
          label={t("firstName")}
          margin="normal"
          style={{ minWidth: 400 }}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          label={t("lastName")}
          margin="normal"
          style={{ minWidth: 400 }}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <TextField
          label={t("email")}
          margin="normal"
          style={{ minWidth: 400 }}
          value={email}
          error={!!email && !/.+@.+\..+/.test(email) || emailAlreadyInUse}
          helperText={
                emailAlreadyInUse ? t("emailInUse") : undefined
              }
          onChange={(e) => setEmail(e.target.value)}
        />
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "phoneVisible") && (
            <TextField
              label={t("phoneNumber")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={phoneNumber}
              error={phoneAlreadyInUse}
              helperText={
                phoneAlreadyInUse ? t("phoneInUse") : undefined
              }
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "companyVisible") && (
            <TextField
              label={t("company")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "taxnumberVisible") && (
            <TextField
              label={t("taxNumber")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={taxNumber}
              onChange={(e) => setTaxNumber(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(
            filteredSettings,
            "occupationalSafetyCardVisible"
          ) && (
            <TextField
              label={t("occupationalSafetyCard")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={occupationalSafetyCard}
              onChange={(e) => setOccupationalSafetyCard(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "hotWorkCardVisible") && (
            <TextField
              label={t("hotWorkCard")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={hotWorkCard}
              onChange={(e) => setHotWorkCard(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "dateOfBirthVisible") && (
            <DatePicker
              label={t("dateOfBirth")}
              value={dateOfBirth}
              sx={{ minWidth: 400, marginTop: theme.spacing(2) }}
              onAccept={(d) => setDateOfBirth(d || dayjs())}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(
            filteredSettings,
            "occupationalSafetyCardImageVisible"
          ) && (
            <Box style={{ minWidth: 400 }}>
              <ImageUploadField
                edit
                label={t("occupationalSafetyCardImage")}
                content={occupationalSafetyCardImage}
                onValueChanged={(val) => {
                  setOccupationalSafetyCardImage(val);
                }}
              />
            </Box>
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "firstAidCardImageVisible") && (
            <Box style={{ minWidth: 400 }}>
              <ImageUploadField
                edit
                label={t("firstAidCardImage")}
                content={firstAidCardImage}
                onValueChanged={(val) => {
                  setFirstAidCardImage(val);
                }}
              />
            </Box>
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "hotWorkCardImageVisible") && (
            <Box style={{ minWidth: 400 }}>
              <ImageUploadField
                edit
                label={t("hotWorkCardImage")}
                content={hotWorkCardImage}
                onValueChanged={(val) => {
                  setHotWorkCardImage(val);
                }}
              />
            </Box>
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "shoeSizeVisible") && (
            <TextField
              label={t("shoeSize")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={shoeSize}
              onChange={(e) => setShoeSize(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "jacketSizeVisible") && (
            <TextField
              label={t("jacketSize")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={jacketSize}
              onChange={(e) => setJacketSize(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "dietaryRestrictionsVisible") && (
            <TextField
              label={t("dietaryRestrictions")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={dietaryRestrictions}
              onChange={(e) => setDietaryRestrictions(e.target.value)}
            />
          )}
        {filteredSettings &&
          isSettingEnabled(filteredSettings, "visitationDateVisible") && (
            <TextField
              label={t("visitationDate")}
              margin="normal"
              style={{ minWidth: 400 }}
              value={visitationDate}
              onChange={(e) => setVisitationDate(e.target.value)}
            />
          )}
        <Button
          style={{ marginTop: theme.spacing(2) }}
          variant="contained"
          disabled={
            (email && !/.+@.+\..+/.test(email)) || !firstName || !lastName || emailAlreadyInUse || phoneAlreadyInUse
          }
          onClick={onAdd}
        >
          {t("save")}
        </Button>
        <Button
          style={{ marginTop: theme.spacing(2) }}
          variant="contained"
          disabled={
            (email && !/.+@.+\..+/.test(email)) || !firstName || !lastName || emailAlreadyInUse || phoneAlreadyInUse
          }
          onClick={() => setShowInviteModal(true)}
        >
          {t("saveAndInvite")}
        </Button>
      </Box>
      {/* Invite modal */}
      <InviteModal
        users={[email]}
        courses={courses || []}
        sendInvite={(users, courses) => {
          console.log(users, courses);
          onAddAndInvite(courses as string[]);
        }}
        isVisible={showInviteModal}
        setVisible={(val) => setShowInviteModal(val)}
      />
    </ViewContainer>
  );
};
